import { SOURCE_SYSTEMS } from './constants'

const StepOrganization = ({
  formData,
  onChange,
  onGoBack,
  isServiceProvider,
  selfOrg,
  clientOrgs,
  loadingOrganizations,
  searchQuery,
  onSearchChange,
  createNewClient,
  onCreateNewClientChange
}) => {
  const handleOrganizationSelect = (orgId) => {
    onChange({ tenancy_id: orgId })
    onCreateNewClientChange(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    onChange({ [name]: value })
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#111418] flex items-center gap-2">
        <span className="material-symbols-outlined text-primary">domain</span>
        Select Organization
      </h2>

      {/* Summary of Step 1 */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-gray-400">description</span>
          <div>
            <p className="text-sm font-bold text-[#111418]">{formData.name}</p>
            <p className="text-xs text-gray-500">{SOURCE_SYSTEMS.find(s => s.id === formData.source_system)?.name} → Odoo</p>
          </div>
        </div>
        <button type="button" onClick={onGoBack} className="text-xs text-primary hover:underline">Change</button>
      </div>

      {/* My Organization (Self) */}
      {selfOrg && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#111418]">My Organization</label>
          <button
            type="button"
            onClick={() => handleOrganizationSelect(selfOrg.id)}
            className={`w-full flex items-center gap-3 p-4 text-left rounded-lg border transition-colors ${
              formData.tenancy_id === selfOrg.id
                ? 'bg-primary/5 border-primary'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold">
              {selfOrg.name[0]?.toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-[#111418]">{selfOrg.name}</p>
              <p className="text-xs text-gray-500">Your organization</p>
            </div>
            {formData.tenancy_id === selfOrg.id && (
              <span className="material-symbols-outlined text-primary">check_circle</span>
            )}
          </button>
        </div>
      )}

      {/* Divider between self and clients - only show if there are clients */}
      {isServiceProvider && clientOrgs.length > 0 && (
        <div className="relative flex py-2 items-center">
          <div className="flex-grow border-t border-gray-200"></div>
          <span className="flex-shrink mx-4 text-gray-500 text-xs font-bold uppercase tracking-wider">Or select a client</span>
          <div className="flex-grow border-t border-gray-200"></div>
        </div>
      )}

      {/* Client Search - only for service providers with clients */}
      {isServiceProvider && (clientOrgs.length > 0 || searchQuery) && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-[#111418]">Client Organizations</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="material-symbols-outlined text-gray-400">search</span>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-sm"
              placeholder="Search clients by name..."
            />
          </div>
        </div>
      )}

      {/* Client List */}
      {isServiceProvider && (
        loadingOrganizations ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : clientOrgs.length > 0 ? (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
            {clientOrgs.map(client => (
              <button
                key={client.id}
                type="button"
                onClick={() => handleOrganizationSelect(client.id)}
                className={`w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors ${
                  formData.tenancy_id === client.id ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                }`}
              >
                <div className="size-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                  {client.name[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#111418]">{client.name}</p>
                  <p className="text-xs text-gray-500">Client Organization</p>
                </div>
                {formData.tenancy_id === client.id && (
                  <span className="material-symbols-outlined text-primary">check_circle</span>
                )}
              </button>
            ))}
          </div>
        ) : searchQuery ? (
          <div className="text-center py-4 text-gray-500">
            No clients match your search.
          </div>
        ) : null
      )}

      {/* Create New Client - only for service providers */}
      {isServiceProvider && (
        <>
          <div className="relative flex py-2 items-center">
            <div className="flex-grow border-t border-gray-200"></div>
            <span className="flex-shrink mx-4 text-gray-500 text-xs font-bold uppercase tracking-wider">Or</span>
            <div className="flex-grow border-t border-gray-200"></div>
          </div>

          <div className={`rounded-xl p-6 border transition-all ${
            createNewClient
              ? 'bg-primary/5 border-primary/20'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-md font-bold flex items-center gap-2 ${
                createNewClient ? 'text-primary' : 'text-gray-600'
              }`}>
                <span className="material-symbols-outlined">person_add</span>
                Create New Client
              </h3>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={createNewClient}
                  onChange={(e) => {
                    onCreateNewClientChange(e.target.checked)
                    if (e.target.checked) {
                      onChange({ tenancy_id: '' })
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            {createNewClient && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-[#111418] uppercase tracking-wider">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="new_client_name"
                    value={formData.new_client_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary outline-none text-sm"
                    placeholder="e.g. Stark Industries"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-[#111418] uppercase tracking-wider">Contact Email</label>
                  <input
                    type="email"
                    name="new_client_email"
                    value={formData.new_client_email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary outline-none text-sm"
                    placeholder="finance@company.com"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-[#111418] uppercase tracking-wider">Industry</label>
                  <select
                    name="new_client_industry"
                    value={formData.new_client_industry}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary outline-none text-sm"
                  >
                    <option value="">Select industry...</option>
                    <option value="technology">Technology / SaaS</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="services">Professional Services</option>
                    <option value="retail">Retail / E-commerce</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="finance">Finance / Banking</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-[#111418] uppercase tracking-wider">Primary Currency</label>
                  <select
                    name="new_client_currency"
                    value={formData.new_client_currency}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary outline-none text-sm"
                  >
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default StepOrganization
